import L from "leaflet";
import _ from "lodash";
import { useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import { Marker, Tooltip } from "react-leaflet";
import { DeliveryType } from "../../shared/types";
import { useStore } from "../store";
import { Segment, DeliveryGroup } from "../types";
import { getGroupKey, getKeyLatLon, keyToLatLon } from "../utils";
import { NumberedIcon } from "./Icons/NumberedIcon";

interface MarkerPlannerProps {
  segmentsMarker: Segment[];
  keyArrivalPoint: string;
  openPopup: (key: string, x: number, y: number) => void;
  allSegments: Segment[];
  weigthPackageAlertOnCluster: (keyArrivalPoint: string) => void;
  setHightlightClusterKey: (listClusterKeys: string[]) => void;
}

/**
 * Tipologie di “ruolo” del punto (cosa rappresenta in quella coordinata).
 */
type PoiKind =
  | "starting"
  | "withdrawRecurrent"
  | "withdrawSpot"
  | "back"
  | "delivery"
  | "return"
  | "placeholder";

/**
 * Config per ogni tipo: colore, label e priorità (per decidere il “tipo principale”).
 */
const TYPE_CONFIG: Record<
  PoiKind,
  { color: string; label: string; priority: number }
> = {
  starting: { color: "#9e9e9e", label: "S", priority: 10 },
  delivery: { color: "#2196f3", label: "D", priority: 20 },
  withdrawRecurrent: { color: "#b30300ff", label: "W", priority: 30 },
  withdrawSpot: { color: "#ff0000ff", label: "W", priority: 40 },
  back: { color: "#fb8c00", label: "B", priority: 50 },
  return: { color: "#26c6da", label: "R", priority: 60 },
  placeholder: { color: "#fdd835", label: "P", priority: 70 },
};

interface MultiTypeIconVisual {
  color: string;
  label: string;
}

interface MultiTypeIconProps {
  kinds: PoiKind[];
  hasNotes: boolean;
  isHeavy: boolean;
  isActive: boolean;
}

const getPieBackground = (kinds: PoiKind[]): string => {
  if (kinds.length === 0) {
    return TYPE_CONFIG.delivery.color;
  }
  const slice = 100 / kinds.length;
  let current = 0;
  const parts: string[] = [];

  kinds.forEach((k) => {
    const start = current;
    const end = current + slice;
    current = end;
    const color = TYPE_CONFIG[k].color;
    parts.push(`${color} ${start}% ${end}%`);
  });

  return `conic-gradient(${parts.join(", ")})`;
};

/**
 * Componente visuale pin:
 * - parte tonda in alto con torta
 * - buco bianco centrale
 * - coda a goccia in basso
 */
const MultiTypeIcon = ({
  kinds,
  hasNotes,
  isHeavy,
  isActive,
}: MultiTypeIconProps) => {
  const effectiveKinds = kinds.length === 0 ? (["delivery"] as PoiKind[]) : kinds;
  const mainKind = TYPE_CONFIG[effectiveKinds[0]];
  const pieBackground = getPieBackground(effectiveKinds);

  const pinClassNames = [
    "poi-pin",
    isHeavy ? "poi-pin-heavy" : "",
    isActive ? "poi-pin-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={pinClassNames} style={{ color: mainKind.color }}>
      <div
        className="poi-pin-circle"
        style={{ backgroundImage: pieBackground }}
      >
        <div className="poi-pin-hole" />
        {hasNotes && <span className="poi-pin-note">!</span>}
      </div>
    </div>
  );
};
const buildMultiTypeDivIcon = (
  activeKinds: PoiKind[],
  hasNotes: boolean,
  isHeavy: boolean,
  isActive: boolean
) => {
  const normalizedKinds =
    activeKinds.length > 3 ? activeKinds.slice(0, 3) : activeKinds;

  const html = ReactDOMServer.renderToString(
    <MultiTypeIcon
      kinds={normalizedKinds}
      hasNotes={hasNotes}
      isHeavy={isHeavy}
      isActive={isActive}
    />
  );

  return L.divIcon({
    html,
    className: "multi-type-icon",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -30],
  });
};

export const MarkerPlanner = ({
  segmentsMarker,
  keyArrivalPoint,
  openPopup,
  allSegments,
  weigthPackageAlertOnCluster,
  setHightlightClusterKey,
}: MarkerPlannerProps) => {
  const {
    addedSegments,
    groupedDeliveries,
    subjectByCoordsMap,
    addDeliveries,
    deleteSegments,
  } = useStore();

  const icon = useMemo(() => {
    const getIcon = (
      cluster: Segment[],
      addedSegments: Segment[],
      groupedDeliveries: DeliveryGroup[],
      clusterKey: string
    ) => {
      // Statistiche base del cluster: peso, colli, note
      let totW = 0;
      let totPN = 0;
      let hasNotes = false;

      for (const segment of cluster) {
        totW += segment.delivery.weight ? segment.delivery.weight : 0;
        totPN += segment.delivery.packages_number
          ? segment.delivery.packages_number
          : 0;
        if (segment.operative_notes) {
          hasNotes = true;
        }
      }

      const isHeavy = totPN >= 21 || totW >= 150;

      const getPreIcon = (
        cluster: Segment[],
        addedSegments: Segment[],
        groupedDeliveries: DeliveryGroup[],
        clusterKey: string,
        hasNotes: boolean,
        isHeavy: boolean
      ) => {
        const sequences: number[] = [];

        // 1) SEQUENZE (NumberedIcon) se il punto è parte di una o più missioni
        const segmentsInCommon = _.intersectionBy(addedSegments, cluster, "id");

        segmentsInCommon
          .filter(
            (segment) =>
              clusterKey ===
              getKeyLatLon(
                segment.arrival_point.latitude,
                segment.arrival_point.longitude
              )
          )
          .reduce((accSequences, segment) => {
            const indexGroup = groupedDeliveries.findIndex(
              (deliveryGroup) => deliveryGroup.id === getGroupKey(segment)
            );
            if (indexGroup !== -1 && !accSequences.includes(indexGroup + 1)) {
              accSequences.push(indexGroup + 1);
            }
            return accSequences;
          }, sequences);

        const containsDeliveries = sequences.length > 0;

        // Aggiunge le sequenze per i punti di ritiro (pick-up)
        groupedDeliveries.forEach((deliveryGroup, index) => {
          if (
            deliveryGroup.isPickUpPoint &&
            getKeyLatLon(
              deliveryGroup.latitude,
              deliveryGroup.longitude
            ) === clusterKey
          ) {
            sequences.push(index + 1);
          }
        });

        sequences.sort();

        if (sequences.length > 0) {
          // Icona “numericata” con NumberedIcon
          return L.divIcon({
            className: "number-icon",           // solo per eventuali stili extra
            iconSize: [28, 40],
            iconAnchor: [14, 40],
            popupAnchor: [0, -30],
            html: ReactDOMServer.renderToString(
              <NumberedIcon
                sequences={sequences}
                starting={!containsDeliveries}
                underline={isHeavy}             // lo usiamo come "isHeavy"
                has_notes={hasNotes}
              />
            ),
          });
        }

        // 2) Multi–tipo dinamico
        const anySegmentDeliveryActive = addedSegments.some((addedSegment) =>
          cluster.some(
            (segment) => addedSegment.delivery_id === segment.delivery_id
          )
        );

        let isStartingPoint = false;
        let isWithdrawRecurrent = false;
        let isWithdrawSpot = false;
        let isBack = false;
        let isDelivery = false;
        let isReturn = false;
        let isPlaceHolder = false;

        for (const segment of cluster) {
          const keyStartingPoint = getKeyLatLon(
            segment.starting_point.latitude,
            segment.starting_point.longitude
          );
          const keyArrivalPoint = getKeyLatLon(
            segment.arrival_point.latitude,
            segment.arrival_point.longitude
          );

          if (keyStartingPoint === clusterKey) {
            if (segment.delivery_type === DeliveryType.WITHDRAW) {
              if (segment.recurrence_delivery === true) {
                isWithdrawRecurrent = true;
              }
              else {
                isWithdrawSpot = true;
              }
            } else {
              isStartingPoint = true;
            }
          }

          if (keyArrivalPoint === clusterKey) {
            if (segment.delivery_type === DeliveryType.WITHDRAW) {
              // ritiro in arrivo → considerato come “punto di partenza”
              isStartingPoint = true;
            } else if (segment.delivery_type === DeliveryType.PLACEHOLDER) {
              isPlaceHolder = true;
            } else if (segment.delivery_type === DeliveryType.RETURN) {
              isReturn = true;
            } else if (segment.delivery_type === DeliveryType.BACK) {
              isBack = true;
            } else {
              isDelivery = true;
            }
          }
        }

        const activeKinds: PoiKind[] = [];
        if (isStartingPoint) activeKinds.push("starting");
        if (isWithdrawRecurrent) activeKinds.push("withdrawRecurrent");
        if (isWithdrawSpot) activeKinds.push("withdrawSpot");
        if (isBack) activeKinds.push("back");
        if (isDelivery) activeKinds.push("delivery");
        if (isReturn) activeKinds.push("return");
        if (isPlaceHolder) activeKinds.push("placeholder");

        // Se ci sono più di 3 tipi diversi, taglio dopo i primi 3 (per non saturare la grafica)
        const normalizedKinds = activeKinds.length > 3 ? activeKinds.slice(0, 3) : activeKinds;

        return buildMultiTypeDivIcon(
          normalizedKinds,
          hasNotes,
          isHeavy,
          anySegmentDeliveryActive
        );
      };

      return getPreIcon(
        cluster,
        addedSegments,
        groupedDeliveries,
        clusterKey,
        hasNotes,
        isHeavy
      );
    };

    return getIcon(
      segmentsMarker,
      addedSegments,
      groupedDeliveries,
      keyArrivalPoint
    );
  }, [addedSegments, groupedDeliveries, keyArrivalPoint, segmentsMarker]);

  return (
    <Marker
      icon={icon}
      eventHandlers={{
        click: (e) => {
          addDeliveries(keyArrivalPoint);
          weigthPackageAlertOnCluster(keyArrivalPoint);
        },
        contextmenu: (e) => {
          if (e.originalEvent.ctrlKey) {
            deleteSegments(segmentsMarker);
          } else {
            openPopup(keyArrivalPoint, e.containerPoint.x, e.containerPoint.y);
          }
        },
        mouseout: () => setHightlightClusterKey([]),
        mouseover: (e) => {
          const listLatLonKeys: string[] = [];
          allSegments.reduce((accKeys: string[], segment: Segment) => {
            const segmentArrivalPointKey = getKeyLatLon(
              segment.arrival_point.latitude,
              segment.arrival_point.longitude
            );
            const segmentStartingPointKey = getKeyLatLon(
              segment.starting_point.latitude,
              segment.starting_point.longitude
            );

            if (
              keyArrivalPoint !== segmentArrivalPointKey &&
              keyArrivalPoint === segmentStartingPointKey &&
              !accKeys.includes(segmentStartingPointKey)
            ) {
              accKeys.push(segmentArrivalPointKey);
            }

            if (
              keyArrivalPoint !== segmentStartingPointKey &&
              keyArrivalPoint === segmentArrivalPointKey &&
              !accKeys.includes(segmentStartingPointKey)
            ) {
              accKeys.push(segmentStartingPointKey);
            }

            return accKeys;
          }, listLatLonKeys);
          setHightlightClusterKey(listLatLonKeys);
        },
      }}
      position={keyToLatLon(keyArrivalPoint)}
    >
      <Tooltip>
        {`${subjectByCoordsMap[keyArrivalPoint].business_name}, ${subjectByCoordsMap[keyArrivalPoint].locality
          } ${subjectByCoordsMap[keyArrivalPoint].province}`}
      </Tooltip>
    </Marker>
  );
};
